// =============================================================================
// onboarding.js – First-run wizard for personalizing the app
// =============================================================================
// Shows a fullscreen step-by-step wizard on first launch. Produces a
// `userProfile` object that is merged into state.settings and synced to cloud.
// If the user skips or has already completed onboarding, the app works
// identically to before (all existing defaults from config.js apply).
// =============================================================================

import { getStopName } from "./line-config.js";
import { TRAIN_STATIONS } from "../data/trains.js";

const CITY_MAIN_STATION = {
  BT: "Canegrate",
  VC: "Canegrate",
  DG: "Legnano",
  AC: "Legnano",
  CZ: "Parabiago",
  LG: "Legnano",
  PB: "Parabiago",
  BS: "Busto Arsizio"
};

const CITY_MAIN_STATION_CODE = {
  BT: "CN_FS",
  VC: "CN_FS",
  DG: "LG_FS",
  AC: "LG_FS",
  CZ: "PB_FS",
  LG: "LG_FS",
  PB: "PB_FS",
  BS: "BS_FS"
};

// ── Zone definitions (Busto Garolfo) ────────────────────────────────────────
const HOME_ZONES = [
  {
    id: "via_buonarroti",
    label: "Via Buonarroti / Centro",
    description: "Fermata principale BT703 – ~3 min a piedi",
    stop: "BT703",
    walkMinutes: 3
  },
  {
    id: "via_rossini",
    label: "Via Rossini / Sud",
    description: "Fermata BT775 – ~5 min a piedi",
    stop: "BT775",
    walkMinutes: 5
  },
  {
    id: "via_montebianco",
    label: "Via Montebianco / Nord",
    description: "Fermata BT956 – ~8 min a piedi",
    stop: "BT956",
    walkMinutes: 8
  },
  {
    id: "piscina",
    label: "Piscina / Via Busto Arsizio",
    description: "Fermate BT949/BT951 – ~4 min a piedi",
    stop: "BT949",
    walkMinutes: 4
  },
  {
    id: "deposito",
    label: "Deposito / Via Busto A. 131",
    description: "Fermata BT999 – ~5 min a piedi",
    stop: "BT999",
    walkMinutes: 5
  }
];

// ── Destination → lines mapping ─────────────────────────────────────────────
const DESTINATIONS = [
  {
    id: "milano_m1",
    label: "Milano via M1 (Molino Dorino)",
    emoji: "🚇",
    lines: ["Z649", "Z601", "Z617", "Z620", "Z621", "Z606"],
    lineLabel: "Z649 · Z601 · Z617 · Z620 · Z621 · Z606"
  },
  {
    id: "milano_a8",
    label: "Milano via autostrada A8",
    emoji: "🛣️",
    lines: ["Z602", "Z603", "Z6C3"],
    lineLabel: "Z602 · Z603 · Z6C3"
  },
  {
    id: "milano_s5_pregnana",
    label: "Milano via S5/S6 (Pregnana FS)",
    emoji: "🚄",
    lines: ["Z649", "Z616"],
    lineLabel: "Z649 · Z616"
  },
  {
    id: "legnano",
    label: "Legnano FS",
    emoji: "🚄",
    lines: ["Z627", "Z642", "Z601", "Z611", "Z612", "Z636"],
    lineLabel: "Z627 · Z642 · Z601 · Z611 · Z636"
  },
  {
    id: "parabiago",
    label: "Parabiago FS",
    emoji: "🚄",
    lines: ["Z644", "Z611"],
    lineLabel: "Z644 · Z611"
  },
  {
    id: "busto_arsizio",
    label: "Busto Arsizio FS",
    emoji: "🚄",
    lines: ["Z625"],
    lineLabel: "Z625"
  },
  {
    id: "magenta",
    label: "Magenta",
    emoji: "🏘️",
    lines: ["Z620", "Z641", "Z642", "Z646"],
    lineLabel: "Z620 · Z641 · Z642 · Z646"
  },
  {
    id: "rho",
    label: "Rho",
    emoji: "🏘️",
    lines: ["Z601", "Z606", "Z616", "Z618"],
    lineLabel: "Z601 · Z606 · Z616 · Z618"
  },
  {
    id: "lainate_arese",
    label: "Lainate / Arese (Il Centro)",
    emoji: "🛍️",
    lines: ["Z612", "Z617"],
    lineLabel: "Z612 · Z617"
  },
  {
    id: "cuggiono",
    label: "Cuggiono / Inveruno",
    emoji: "🌾",
    lines: ["Z621", "Z622", "Z627", "Z646"],
    lineLabel: "Z621 · Z622 · Z646"
  },
  {
    id: "vittuone",
    label: "Vittuone / Corbetta",
    emoji: "🌾",
    lines: ["Z620", "Z622", "Z643"],
    lineLabel: "Z620 · Z622 · Z643"
  },
  {
    id: "castano",
    label: "Castano Primo / Arconate",
    emoji: "🏫",
    lines: ["Z647", "Z636", "Z641", "Z646"],
    lineLabel: "Z647 · Z636 · Z641 · Z646"
  },
  {
    id: "canegrate_auto",
    label: "Canegrate FS in auto",
    emoji: "🚗",
    lines: [],
    lineLabel: "S5 treno"
  }
];

// ── Favorite stop derivation per zone ───────────────────────────────────────
// Given a homeZone, derive the best favorite stops for each line/direction.
// Falls back to config.js defaults if not mapped here.
const ZONE_FAVORITES = {
  via_buonarroti: {
    Z649: { outbound: "BT703", return: "BT956" },
    Z627: { outbound: "BT301", return: "BT947" },
    Z644: { outbound: "BT703", return: "BT956" },
    Z625: { outbound: "BT947", return: "BT301" },
    Z647: { outbound: "BT956", return: "BT703" },
    Z642: { outbound: "BT703", return: "BT775" }
  },
  via_rossini: {
    Z649: { outbound: "BT775", return: "BT956" },
    Z627: { outbound: "BT301", return: "BT947" },
    Z644: { outbound: "BT775", return: "BT956" },
    Z625: { outbound: "BT947", return: "BT301" },
    Z647: { outbound: "BT956", return: "BT775" },
    Z642: { outbound: "BT956", return: "BT775" }
  },
  via_montebianco: {
    Z649: { outbound: "BT956", return: "BT956" },
    Z627: { outbound: "BT703", return: "BT947" },
    Z644: { outbound: "BT775", return: "BT956" },
    Z625: { outbound: "BT947", return: "BT301" },
    Z647: { outbound: "BT956", return: "BT775" },
    Z642: { outbound: "BT956", return: "BT775" }
  },
  piscina: {
    Z649: { outbound: "BT949", return: "BT951" },
    Z627: { outbound: "BT703", return: "BT947" },
    Z644: { outbound: "BT775", return: "BT956" },
    Z625: { outbound: "BT947", return: "BT301" },
    Z647: { outbound: "BT956", return: "BT775" },
    Z642: { outbound: "BT956", return: "BT775" }
  },
  deposito: {
    Z649: { outbound: "BT999", return: "BT999" },
    Z627: { outbound: "BT703", return: "BT947" },
    Z644: { outbound: "BT775", return: "BT956" },
    Z625: { outbound: "BT947", return: "BT301" },
    Z647: { outbound: "BT956", return: "BT775" },
    Z642: { outbound: "BT956", return: "BT775" }
  }
};

// ── Wizard State ────────────────────────────────────────────────────────────
let wizardState = {
  step: 1,
  userType: null, // "resident" | "visitor" | "skip"
  focusCity: "BT", // focus city code
  homeZone: null, // zone id
  destinations: [], // destination ids
  useCanegrate: false,
  driveCanegrate: 16,
  trainReachMinutes: 0,
  activeLines: [],
  favoriteStops: {},
  walkMinutes: 6,
  homeStop: null,
  wantNotifications: false,
  visibleTrains: ["CN_FS"],
  liveHero: "Z649"
};

let overlayEl = null;
let onCompleteCallback = null;

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Check if onboarding should be shown.
 */
export function shouldShowOnboarding(settings) {
  return !settings?.userProfile?.onboardingVersion;
}

/**
 * Start the onboarding wizard.
 * @param {Function} onComplete - called with the resulting userProfile object
 */
export function startOnboarding(onComplete) {
  onCompleteCallback = onComplete;
  wizardState = {
    step: 1,
    userType: null,
    focusCity: "BT",
    homeZone: null,
    destinations: [],
    useCanegrate: false,
    driveCanegrate: 16,
    trainReachMinutes: 0,
    activeLines: [],
    favoriteStops: {},
    walkMinutes: 6,
    homeStop: null,
    wantNotifications: false,
    visibleTrains: ["CN_FS"],
    liveHero: "Z649"
  };
  showOverlay();
  renderStep();
}

/**
 * Dismiss the wizard without saving (skip).
 */
export function dismissOnboarding() {
  hideOverlay();
  if (onCompleteCallback) onCompleteCallback(null);
}

// ── Overlay Management ──────────────────────────────────────────────────────

function showOverlay() {
  if (overlayEl) {
    overlayEl.remove();
  }
  overlayEl = document.createElement("div");
  overlayEl.className = "onboarding-overlay";
  overlayEl.setAttribute("role", "dialog");
  overlayEl.setAttribute("aria-modal", "true");
  overlayEl.setAttribute("aria-label", "Configurazione iniziale");
  document.body.appendChild(overlayEl);
  requestAnimationFrame(() => overlayEl.classList.add("open"));
}

function hideOverlay() {
  if (!overlayEl) return;
  overlayEl.classList.remove("open");
  setTimeout(() => {
    overlayEl?.remove();
    overlayEl = null;
  }, 350);
}

// ── Step Rendering ──────────────────────────────────────────────────────────

function renderStep() {
  if (!overlayEl) return;
  const totalSteps = wizardState.userType === "visitor" ? 4 : 5;
  const currentStep = wizardState.step;

  let content = "";
  switch (currentStep) {
    case 1:
      content = renderStep1();
      break;
    case 2:
      content = wizardState.userType === "visitor" ? renderStep3() : renderStep2();
      break;
    case 3:
      content = wizardState.userType === "visitor" ? renderStep4() : renderStep3();
      break;
    case 4:
      content = wizardState.userType === "visitor" ? renderStep5() : renderStep4();
      break;
    case 5:
      content = renderStep5();
      break;
  }

  const progress = Math.round((currentStep / totalSteps) * 100);

  overlayEl.innerHTML = `
    <div class="onboarding-card">
      <div class="onboarding-progress">
        <div class="onboarding-progress-bar" style="width: ${progress}%"></div>
      </div>
      <div class="onboarding-step-indicator">${currentStep} / ${totalSteps}</div>
      ${content}
    </div>
  `;

  bindStepEvents();
}

// ── Step 1: Who are you? ────────────────────────────────────────────────────

function renderStep1() {
  return `
    <div class="onboarding-content">
      <h2 class="onboarding-title">Benvenuto! 👋</h2>
      <p class="onboarding-subtitle">Seleziona la tua città di focus e personalizziamo l'app in pochi secondi.</p>
      
      <div style="background: rgba(34, 211, 238, 0.05); border: 1px solid rgba(34, 211, 238, 0.18); border-radius: 8px; padding: 10px 14px; margin-bottom: 20px; font-size: 0.78rem; color: var(--muted); line-height: 1.4; display: flex; align-items: center; gap: 8px;">
        <span>🔒</span>
        <span>I tuoi dati rimangono memorizzati sul tuo dispositivo. Se decidi di attivare la sincronizzazione cloud, verranno crittografati in modalità <strong>Zero-Knowledge</strong> sul tuo browser prima del caricamento. <a href="./privacy.html" target="_blank" style="color: var(--accent); text-decoration: underline; font-weight: 500;">Leggi la Privacy Policy & GDPR</a>.</span>
      </div>

      <div class="onboarding-field" style="margin-bottom: 20px;">

        <label class="onboarding-field-label">Città di Focus principale</label>
        <select class="onboarding-field-input" data-focus-city style="min-height: 40px; font-size: 0.95rem; width: 100%;">
          <option value="BT" selected>🏠 Busto Garolfo</option>
          <option value="VC">🏡 Villa Cortese</option>
          <option value="DG">🏡 Dairago</option>
          <option value="AC">🏡 Arconate</option>
          <option value="CZ">🏡 Casorezzo</option>
          <option value="LG">🏢 Legnano</option>
          <option value="PB">🏢 Parabiago</option>
          <option value="BS">🏢 Busto Arsizio</option>
        </select>
      </div>

      <div class="onboarding-choices">
        <button type="button" class="onboarding-choice" data-user-type="resident">
          <span class="onboarding-choice-emoji">🏠</span>
          <span class="onboarding-choice-label">Vivo qui</span>
          <span class="onboarding-choice-desc">Configurazione completa personalizzata</span>
        </button>
        <button type="button" class="onboarding-choice" data-user-type="visitor">
          <span class="onboarding-choice-emoji">🚌</span>
          <span class="onboarding-choice-label">Frequento spesso la zona</span>
          <span class="onboarding-choice-desc">Scegli le linee che usi di più</span>
        </button>
        <button type="button" class="onboarding-choice" data-user-type="skip" style="border-style: dashed; border-color: rgba(var(--accent-rgb), 0.45); background: rgba(var(--accent-rgb), 0.05);">
          <span class="onboarding-choice-emoji">⚡</span>
          <span class="onboarding-choice-label">Sto solo provando</span>
          <span class="onboarding-choice-desc">Accedi subito con le impostazioni predefinite</span>
        </button>
      </div>
    </div>
  `;
}

// ── Step 2: Home zone (residents only) ──────────────────────────────────────

function renderStep2() {
  return `
    <div class="onboarding-content">
      <h2 class="onboarding-title">Da dove parti? 📍</h2>
      <p class="onboarding-subtitle">Scegli la zona più vicina a casa tua per calcolare i tempi a piedi.</p>
      <div class="onboarding-choices">
        ${HOME_ZONES.map(
          (zone) => `
          <button type="button" class="onboarding-choice ${wizardState.homeZone === zone.id ? "selected" : ""}" data-home-zone="${zone.id}">
            <span class="onboarding-choice-label">${zone.label}</span>
            <span class="onboarding-choice-desc">${zone.description}</span>
          </button>
        `
        ).join("")}
      </div>
      <div class="onboarding-nav">
        <button type="button" class="onboarding-btn secondary" data-back>← Indietro</button>
        <button type="button" class="onboarding-btn primary" data-next ${!wizardState.homeZone ? "disabled" : ""}>Avanti →</button>
      </div>
    </div>
  `;
}

// ── Step 3: Destinations ────────────────────────────────────────────────────

function renderStep3() {
  return `
    <div class="onboarding-content">
      <h2 class="onboarding-title">Dove vai di solito? 🎯</h2>
      <p class="onboarding-subtitle">Seleziona una o più destinazioni. Attiveremo le linee giuste.</p>
      <div class="onboarding-choices">
        ${DESTINATIONS.map(
          (dest) => `
          <button type="button" class="onboarding-choice onboarding-choice--compact ${wizardState.destinations.includes(dest.id) ? "selected" : ""}" data-destination="${dest.id}">
            <span class="onboarding-choice-emoji">${dest.emoji}</span>
            <span class="onboarding-choice-label">${dest.label} <small style="opacity: 0.7; font-weight: 600; font-size: 0.72rem; margin-left: 4px;">${dest.lineLabel}</small></span>
          </button>
        `
        ).join("")}
      </div>
      <div class="onboarding-nav">
        <button type="button" class="onboarding-btn secondary" data-back>← Indietro</button>
        <button type="button" class="onboarding-btn primary" data-next ${wizardState.destinations.length === 0 ? "disabled" : ""}>Avanti →</button>
      </div>
    </div>
  `;
}

// ── Step 4: Summary Profile Card (OB-1) ─────────────────────────────────────

function renderStep4() {
  // Derive active lines from destinations
  const lines = deriveActiveLines();
  const zone = HOME_ZONES.find((z) => z.id === wizardState.homeZone);
  const zoneFavs = ZONE_FAVORITES[wizardState.homeZone] || {};
  const cityName = CITY_MAIN_STATION[wizardState.focusCity || "BT"] ? (wizardState.focusCity === "BT" ? "Busto Garolfo" : wizardState.focusCity) : "Busto Garolfo";
  const mainStationName = CITY_MAIN_STATION[wizardState.focusCity || "BT"] || "Canegrate";

  // Ensure favoriteStops are populated with defaults
  if (Object.keys(wizardState.favoriteStops).length === 0 && wizardState.homeZone) {
    wizardState.favoriteStops = structuredClone(zoneFavs);
  }

  const activeLinesForHero =
    lines.length > 0 ? lines : ["Z649", "Z627", "Z644", "Z625", "Z647", "Z642"];
  const heroOptionsHtml = [
    ...activeLinesForHero.map(
      (id) =>
        `<option value="${id}" ${wizardState.liveHero === id ? "selected" : ""}>Bus ${id}</option>`
    ),
    ...Object.entries(TRAIN_STATIONS).map(
      ([code, info]) =>
        `<option value="${code}" ${wizardState.liveHero === code ? "selected" : ""}>Treno - Stazione di ${info.name}</option>`
    )
  ].join("");

  return `
    <div class="onboarding-content">
      <h2 class="onboarding-title">Il tuo profilo 👤</h2>
      <p class="onboarding-subtitle">Abbiamo configurato le migliori impostazioni per te in base alle tue scelte.</p>

      <div class="onboarding-profile-card" style="background: rgba(255, 255, 255, 0.03); border: 1px solid var(--line); border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 14px; margin-bottom: 16px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid var(--line); padding-bottom: 10px;">
          <div>
            <span style="font-size: 0.72rem; text-transform: uppercase; color: var(--muted); font-weight: 700; letter-spacing: 0.05em;">Partenza da</span>
            <div style="font-size: 1.05rem; font-weight: 750; color: var(--text);">${cityName}${zone ? ` · ${zone.label}` : ""}</div>
          </div>
          <span class="badge" style="font-size: 0.75rem; background: rgba(34, 211, 238, 0.1); color: var(--accent); border: 1px solid rgba(34, 211, 238, 0.2); padding: 4px 8px; border-radius: 6px;">🚶 ${wizardState.walkMinutes} min</span>
        </div>

        <div>
          <span style="font-size: 0.72rem; text-transform: uppercase; color: var(--muted); font-weight: 700; letter-spacing: 0.05em;">Linee Suggerite (${lines.length})</span>
          <div style="display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px;">
            ${lines.map((l) => `<span style="background: rgba(255,255,255,0.06); border: 1px solid var(--line); border-radius: 6px; padding: 4px 10px; font-weight: 700; font-size: 0.85rem; color: var(--text);">${l}</span>`).join("")}
          </div>
        </div>

        <div>
          <span style="font-size: 0.72rem; text-transform: uppercase; color: var(--muted); font-weight: 700; letter-spacing: 0.05em;">Stazione di riferimento</span>
          <div style="font-size: 0.9rem; font-weight: 600; color: var(--text); margin-top: 4px;">🚆 ${mainStationName} FS</div>
        </div>
      </div>

      <details style="margin-bottom: 16px; background: rgba(255,255,255,0.02); border: 1px solid var(--line); border-radius: 8px; padding: 10px 14px;">
        <summary style="font-size: 0.82rem; font-weight: 600; color: var(--accent); cursor: pointer; user-select: none;">⚙️ Personalizza opzioni avanzate</summary>
        <div style="margin-top: 14px; display: flex; flex-direction: column; gap: 12px;">
          <div class="onboarding-field">
            <label class="onboarding-field-label">Minuti a piedi verso la fermata</label>
            <input type="number" class="onboarding-field-input" data-walk-minutes min="1" max="30" value="${wizardState.walkMinutes}">
          </div>
          <div class="onboarding-field">
            <label class="onboarding-field-label">Scheda principale in alto (HERO)</label>
            <select class="onboarding-field-input" data-onboarding-live-hero style="width: 100%; min-height: 38px;">
              ${heroOptionsHtml}
            </select>
          </div>
        </div>
      </details>

      <p style="font-size: 0.75rem; color: var(--muted); line-height: 1.4; margin: 0 0 16px 0;">
        💡 Le fermate preferite di ogni singola linea sono state preconfigurate e possono essere regolate nelle <strong>Impostazioni</strong>.
      </p>

      <div class="onboarding-nav">
        <button type="button" class="onboarding-btn secondary" data-back>← Indietro</button>
        <button type="button" class="onboarding-btn primary" data-next>Avanti →</button>
      </div>
    </div>
  `;
}

// ── Step 5: Notifications + Google sign-in suggestion + finish ───────────────

function renderStep5() {
  return `
    <div class="onboarding-content">
      <h2 class="onboarding-title">Quasi fatto! 🎉</h2>
      <p class="onboarding-subtitle">Ultime due cose opzionali.</p>

      <div style="margin-bottom: 20px;">
        <p class="onboarding-field-label" style="margin-bottom: 8px;">🔔 Notifiche reminder</p>
        <div class="onboarding-choices">
          <button type="button" class="onboarding-choice onboarding-choice--compact ${wizardState.wantNotifications ? "selected" : ""}" data-notif="yes">
            <span class="onboarding-choice-emoji">✅</span>
            <span class="onboarding-choice-label">Sì, avvisami 5 e 10 min prima</span>
          </button>
          <button type="button" class="onboarding-choice onboarding-choice--compact ${!wizardState.wantNotifications ? "selected" : ""}" data-notif="no">
            <span class="onboarding-choice-emoji">⏭️</span>
            <span class="onboarding-choice-label">No grazie, le attivo dopo</span>
          </button>
        </div>
      </div>

      <div style="padding: 14px; border: 1px solid var(--line); border-radius: var(--radius); background: rgba(var(--accent-rgb), 0.06);">
        <p style="margin: 0 0 6px; font-weight: 750; font-size: 0.88rem; color: var(--text);">☁️ Sincronizza tra dispositivi</p>
        <p style="margin: 0 0 12px; color: var(--muted); font-size: 0.8rem; line-height: 1.4;">Accedi con Google per salvare le preferenze nel cloud e ritrovarle su altri dispositivi. Puoi farlo anche dopo nelle Impostazioni.</p>
        <button type="button" class="onboarding-btn primary" data-google-login style="width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 0.84rem;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Accedi con Google
        </button>
      </div>

      <div class="onboarding-nav">
        <button type="button" class="onboarding-btn secondary" data-back>← Indietro</button>
        <button type="button" class="onboarding-btn primary" data-finish>Inizia! 🚀</button>
      </div>
    </div>
  `;
}

// ── Event Binding ───────────────────────────────────────────────────────────

function bindStepEvents() {
  if (!overlayEl) return;

  // Step 1: user type
  overlayEl.querySelectorAll("[data-user-type]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const citySelect = overlayEl.querySelector("[data-focus-city]");
      wizardState.focusCity = citySelect ? citySelect.value : "BT";
      wizardState.visibleTrains = [CITY_MAIN_STATION_CODE[wizardState.focusCity] || "CN_FS"];
      wizardState.userType = btn.dataset.userType;

      if (wizardState.userType === "skip") {
        finishWizard(true);
      } else if (wizardState.userType === "resident" && wizardState.focusCity === "BT") {
        // Only Busto Garolfo has home zones configured
        wizardState.step = 2;
        renderStep();
      } else {
        // Skip home zone step for other focus cities or visitors
        wizardState.step = 3;
        renderStep();
      }
    });
  });

  // Step 2: home zone
  overlayEl.querySelectorAll("[data-home-zone]").forEach((btn) => {
    btn.addEventListener("click", () => {
      wizardState.homeZone = btn.dataset.homeZone;
      const zone = HOME_ZONES.find((z) => z.id === wizardState.homeZone);
      if (zone) {
        wizardState.walkMinutes = zone.walkMinutes;
        wizardState.homeStop = zone.stop;
      }
      // Pre-derive favorites for this zone
      const zoneFavs = ZONE_FAVORITES[wizardState.homeZone] || {};
      wizardState.favoriteStops = structuredClone(zoneFavs);
      renderStep();
    });
  });

  // Step 3: destinations (multi-select)
  overlayEl.querySelectorAll("[data-destination]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.destination;
      const idx = wizardState.destinations.indexOf(id);
      if (idx === -1) {
        wizardState.destinations.push(id);
      } else {
        wizardState.destinations.splice(idx, 1);
      }
      wizardState.useCanegrate = wizardState.destinations.includes("canegrate_auto");
      wizardState.activeLines = deriveActiveLines();
      renderStep();
    });
  });

  // Step 4: walk minutes input
  const walkInput = overlayEl.querySelector("[data-walk-minutes]");
  if (walkInput) {
    walkInput.addEventListener("change", () => {
      wizardState.walkMinutes = Math.max(1, Math.min(30, Number(walkInput.value) || 6));
    });
  }

  // Step 4: drive canegrate input
  const driveInput = overlayEl.querySelector("[data-drive-canegrate]");
  if (driveInput) {
    driveInput.addEventListener("change", () => {
      wizardState.driveCanegrate = Math.max(1, Math.min(45, Number(driveInput.value) || 16));
    });
  }

  // Step 4: train reach minutes input
  const trainReachInput = overlayEl.querySelector("[data-train-reach]");
  if (trainReachInput) {
    trainReachInput.addEventListener("change", () => {
      wizardState.trainReachMinutes = Math.max(0, Math.min(60, Number(trainReachInput.value) || 0));
    });
  }

  // Step 4: live hero input
  const liveHeroSelect = overlayEl.querySelector("[data-onboarding-live-hero]");
  if (liveHeroSelect) {
    liveHeroSelect.addEventListener("change", () => {
      wizardState.liveHero = liveHeroSelect.value;
    });
  }

  // Step 4: visible train station checkboxes
  overlayEl.querySelectorAll("[data-onboarding-visible-train]").forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      const code = checkbox.dataset.onboardingVisibleTrain;
      if (checkbox.checked) {
        if (!wizardState.visibleTrains.includes(code)) {
          wizardState.visibleTrains.push(code);
        }
      } else {
        wizardState.visibleTrains = wizardState.visibleTrains.filter((c) => c !== code);
      }
    });
  });

  // Step 4: favorite stop selects
  overlayEl.querySelectorAll("[data-fav-stop]").forEach((select) => {
    select.addEventListener("change", () => {
      const [lineId, direction] = select.dataset.favStop.split(":");
      if (!wizardState.favoriteStops[lineId]) wizardState.favoriteStops[lineId] = {};
      wizardState.favoriteStops[lineId][direction] = select.value;
    });
  });

  // Step 5: notifications
  overlayEl.querySelectorAll("[data-notif]").forEach((btn) => {
    btn.addEventListener("click", () => {
      wizardState.wantNotifications = btn.dataset.notif === "yes";
      renderStep();
    });
  });

  // Step 5: Google sign-in (optional, non-blocking)
  overlayEl.querySelector("[data-google-login]")?.addEventListener("click", async () => {
    try {
      const { signInWithGoogle } = await import("./firebase-sync.js");
      const user = await signInWithGoogle();
      if (user) {
        const btn = overlayEl.querySelector("[data-google-login]");
        if (btn) {
          btn.textContent = `✓ Connesso come ${user.displayName || user.email}`;
          btn.disabled = true;
          btn.style.opacity = "0.7";
        }
      }
    } catch (e) {
      const btn = overlayEl.querySelector("[data-google-login]");
      if (btn) btn.textContent = "Errore – riprova nelle Impostazioni";
    }
  });

  // Navigation
  overlayEl.querySelector("[data-back]")?.addEventListener("click", () => {
    wizardState.step = Math.max(1, wizardState.step - 1);
    renderStep();
  });

  overlayEl.querySelector("[data-next]")?.addEventListener("click", () => {
    wizardState.step++;
    renderStep();
  });

  overlayEl.querySelector("[data-finish]")?.addEventListener("click", () => {
    finishWizard(false);
  });
}

// ── Finish & Produce Profile ────────────────────────────────────────────────

function finishWizard(skipped) {
  hideOverlay();

  if (skipped) {
    // Produce a minimal profile that marks onboarding as done but changes nothing
    const profile = {
      onboardingVersion: 1,
      completedAt: new Date().toISOString(),
      userType: "skip",
      skipped: true
    };
    if (onCompleteCallback) onCompleteCallback(profile);
    return;
  }

  const activeLines = deriveActiveLines();
  const zone = HOME_ZONES.find((z) => z.id === wizardState.homeZone);

  // Build timetableStops: include the user's chosen favorite stops in the
  // visible columns for each line/direction/dayType combination.
  const timetableStops = {};
  for (const lineId of activeLines) {
    const favs = wizardState.favoriteStops[lineId];
    if (!favs) continue;
    timetableStops[lineId] = {};
    // For each day type, set the outbound/return columns to include the favorite
    for (const dayType of ["weekday", "saturday", "sunday"]) {
      if (favs.outbound) {
        const key = `${dayType}_outbound`;
        // We don't override — we just ensure the favorite is included.
        // The actual column list will be resolved by settings.js from config defaults
        // merged with this. We set it to null to signal "use default + ensure favorite".
      }
    }
  }

  const profile = {
    onboardingVersion: 1,
    completedAt: new Date().toISOString(),
    userType: wizardState.userType,
    focusCity: wizardState.focusCity || "BT",
    homeZone: wizardState.homeZone || null,
    homeStop: zone?.stop || null,
    walkMinutes: wizardState.walkMinutes,
    stationReachMinutes: {
      [CITY_MAIN_STATION_CODE[wizardState.focusCity || "BT"] || "CN_FS"]:
        wizardState.useCanegrate
          ? wizardState.driveCanegrate
          : (wizardState.trainReachMinutes || 0)
    },
    destinations: [...wizardState.destinations],
    activeLines: activeLines,
    useCanegrate: wizardState.useCanegrate,
    driveCanegrate: wizardState.useCanegrate ? wizardState.driveCanegrate : null,
    favoriteStops: structuredClone(wizardState.favoriteStops),
    wantNotifications: wizardState.wantNotifications,
    visibleTrains: [...wizardState.visibleTrains],
    liveHero: wizardState.liveHero
  };

  if (onCompleteCallback) onCompleteCallback(profile);
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function deriveActiveLines() {
  const lines = new Set();
  for (const destId of wizardState.destinations) {
    const dest = DESTINATIONS.find((d) => d.id === destId);
    if (dest) dest.lines.forEach((l) => lines.add(l));
  }
  // If no lines derived (e.g. only canegrate_auto), show all
  if (
    lines.size === 0 &&
    wizardState.destinations.length > 0 &&
    wizardState.destinations.every((d) => d === "canegrate_auto")
  ) {
    return []; // empty means "show all" in the consumer
  }
  return [...lines];
}
